import { useState, useEffect, useCallback, useRef } from 'react'
import type { UserPresence, PresenceStatus } from '../types'
import { API_URL, getAuthHeaders } from '../api'
import { getToken } from '../api'


export function usePresence() {
  const [myStatus, setMyStatus] = useState('online')
  const [presenceMap, setPresenceMap] = useState<Record<string, UserPresence>>({}) // { userId: { status, last_seen_at } }
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  
  // Update own presence status
  const updateMyStatus = useCallback(async (status: string) => {
    setMyStatus(status)
    try {
      await fetch(`${API_URL}/api/presence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status }),
      })
    } catch {
      // ignore
    }
  }, [])

  // Heartbeat: send "online" every 2 minutes
  useEffect(() => {
    const heartbeat = async () => {
      try {
        await fetch(`${API_URL}/api/presence`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ status: myStatus }),
        })
      } catch {
        // ignore
      }
    }

    // Send immediately
    heartbeat()

    // Then every 2 minutes
    heartbeatRef.current = setInterval(heartbeat, 2 * 60 * 1000)

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    }
  }, [myStatus])

  // Set offline on page unload
  useEffect(() => {
    const handleUnload = () => {
      const token = getToken()
      if (!token) return
      // Use sendBeacon for reliability on unload
      const blob = new Blob([JSON.stringify({ status: 'offline' })], { type: 'application/json' })
      // sendBeacon can't set headers — pass the token as a query param
      navigator.sendBeacon(`${API_URL}/api/presence?token=${encodeURIComponent(token)}`, blob)
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [])

  // Poll all user presences every 15 seconds
  const refreshPresences = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/presence`, {
        headers: getAuthHeaders(),
      })
      const data = await res.json()
      if (res.ok && data.users) {
        const map: Record<string, { status: PresenceStatus; last_seen_at?: string }> = {}
        data.users.forEach((u: { user_id: string; presence_status: PresenceStatus; last_seen_at?: string }) => {
          map[u.user_id] = {
            status: u.presence_status,
            last_seen_at: u.last_seen_at,
          }
        })
        setPresenceMap(map)
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    refreshPresences()
    pollRef.current = setInterval(refreshPresences, 15000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [refreshPresences])

  // Get presence for a specific user
  const getUserPresence = useCallback((userId: string) => {
    return presenceMap[userId] || { status: 'offline', last_seen_at: null }
  }, [presenceMap])

  return { myStatus, updateMyStatus, getUserPresence, refreshPresences }
}
